import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import { DataGrid, GridToolbarQuickFilter, enUS } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MessageIcon from "@mui/icons-material/Message";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmDialogComponent from "./ConfirmDialogComponent";
import SendMessageComponent from "./SendMessageComponent";
import { useTranslation } from "react-i18next";

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
const BORDER = 'rgba(245, 158, 11, 0.18)';

function CustomToolbar() {
  const { t } = useTranslation();
  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
      <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        placeholder={t('students.search')}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.85rem',
            borderRadius: '8px',
            background: '#f8fafc',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: AMBER },
            '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 1.5 },
          },
          '& .MuiInputBase-input': { py: 0.75 },
        }}
      />
    </Box>
  );
}

const StudentsComponent = (props) => {
  const { authTokens, logoutUser, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [studentName, setStudentName] = useState([]);
  const [studentId, setStudentId] = useState([]);

  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [absenceChild, setAbsenceChild] = useState(null);
  const [absenceList, setAbsenceList] = useState([]);
  const [absenceDate, setAbsenceDate] = useState('');
  const [absenceReason, setAbsenceReason] = useState('');
  const [absenceType, setAbsenceType] = useState('full');
  const [skipTo, setSkipTo] = useState(true);
  const [skipFrom, setSkipFrom] = useState(true);
  const [absenceSaving, setAbsenceSaving] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) };

  const openAbsenceDialog = (params) => {
    setAbsenceChild({
      child_id: params.row.child_id,
      name: params.row.firstname + ' ' + params.row.lastname,
      bus_to_school: params.row.bus_to_school,
      bus_from_school: params.row.bus_from_school,
    });
    setAbsenceDate('');
    setAbsenceReason('');
    setAbsenceType('full');
    setSkipTo(true);
    setSkipFrom(true);
    setAbsenceList([]);
    setAbsenceDialogOpen(true);
    fetch(backendUrl + `api/child-absences/?child_id=${params.row.child_id}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setAbsenceList);
  };

  const handleAddAbsence = async () => {
    if (!absenceDate) return;
    setAbsenceSaving(true);
    try {
      const res = await fetch(backendUrl + 'api/child-absences/', {
        method: 'POST', headers,
        body: JSON.stringify({
          child_id: absenceChild.child_id, date: absenceDate, reason: absenceReason,
          absence_type: absenceType === 'full' ? 0 : 1,
          skip_to_school: absenceType === 'bus' ? skipTo : true,
          skip_from_school: absenceType === 'bus' ? skipFrom : true,
        }),
      });
      if (res.ok) {
        setAbsenceDate('');
        setAbsenceReason('');
        fetch(backendUrl + `api/child-absences/?child_id=${absenceChild.child_id}`, { headers })
          .then(r => r.ok ? r.json() : [])
          .then(setAbsenceList);
      }
    } finally {
      setAbsenceSaving(false);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    const res = await fetch(backendUrl + `api/child-absence/${absenceId}/`, { method: 'DELETE', headers });
    if (res.ok) {
      setAbsenceList(prev => prev.filter(a => a.absence_id !== absenceId));
    }
  };

  const sendMessage = (event, params) => {
    setStudentName(params.row.firstname + ' ' + params.row.lastname);
    setStudentId(params.row.child_id);
    setOpen(true);
  };

  const deleteStudent = (event, params) => {
    setStudentId(params.id);
    setStudentName([params.row.firstname + " " + params.row.lastname]);
    setDeleteOpen(true);
  };

  const doDeleteStudent = async () => {
    const response = await fetch(backendUrl + "api/delete-student/", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + String(authTokens.access) },
      body: JSON.stringify({ id: studentId }),
    }).catch(console.error);

    if (response?.status === 200) {
      setDeleteOpen(false);
      props.onStudentDelete();
    } else if (response?.statusText === "Unauthorized") {
      logoutUser();
    }
  };

  const columns = [
    {
      field: "lastname",
      headerName: t('students.last_name'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Link to={`/student/${params.row.id}`} style={{ textDecoration: 'none' }}>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              {params.value}
            </Typography>
          </Link>
          {params.row.is_absent_today && (
            <Chip
              icon={<PersonOffIcon sx={{ fontSize: '0.7rem !important' }} />}
              label={t('absences.absent_today')}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(239,68,68,0.12)', color: '#dc2626', fontWeight: 600, '& .MuiChip-icon': { color: '#dc2626' } }}
            />
          )}
          {(params.row.is_bus_absent_to || params.row.is_bus_absent_from) && (
            <Chip
              icon={<DirectionsBusIcon sx={{ fontSize: '0.7rem !important' }} />}
              label={t('absences.bus_absent_today')}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(249,115,22,0.12)', color: '#c2410c', fontWeight: 600, '& .MuiChip-icon': { color: '#c2410c' } }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "firstname",
      headerName: t('students.first_name'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Link to={`/student/${params.row.id}`} style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
            {params.value}
          </Typography>
        </Link>
      ),
    },
    {
      field: "schoolclass_name",
      headerName: t('students.school_class'),
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.35, borderRadius: '6px', background: 'rgba(15,45,94,0.07)', color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 500 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "department_name",
      headerName: t('students.department'),
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.35, borderRadius: '6px', background: `rgba(245, 158, 11, 0.1)`, color: '#92400e', fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 500 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "bus_to_school",
      headerName: t('students.school_bus'),
      width: 180,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const to = params.row.bus_to_school;
        const from = params.row.bus_from_school;
        const routeTo = params.row.route_to;
        const routeFrom = params.row.route_from;
        const busAbsentTo = params.row.is_bus_absent_to;
        const busAbsentFrom = params.row.is_bus_absent_from;
        if (!to && !from) return <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8' }}>—</Typography>;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {to && (
              <Tooltip title={busAbsentTo ? t('absences.skip_to_school') : (routeTo ? t('students.route_assigned_to', { name: routeTo }) : t('students.route_not_assigned'))} arrow>
                <Chip
                  icon={<DirectionsBusIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label="→"
                  size="small"
                  sx={{
                    fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', border: 'none', height: 22,
                    background: busAbsentTo ? 'rgba(239,68,68,0.12)' : (routeTo ? 'rgba(34,197,94,0.12)' : 'rgba(249,115,22,0.15)'),
                    color: busAbsentTo ? '#dc2626' : (routeTo ? '#15803d' : '#c2410c'),
                    ...(busAbsentTo ? { '& .MuiChip-label': { textDecoration: 'line-through' } } : {}),
                  }}
                />
              </Tooltip>
            )}
            {from && (
              <Tooltip title={busAbsentFrom ? t('absences.skip_from_school') : (routeFrom ? t('students.route_assigned_from', { name: routeFrom }) : t('students.route_not_assigned'))} arrow>
                <Chip
                  icon={<DirectionsBusIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label="←"
                  size="small"
                  sx={{
                    fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', border: 'none', height: 22,
                    background: busAbsentFrom ? 'rgba(239,68,68,0.12)' : (routeFrom ? 'rgba(59,130,246,0.12)' : 'rgba(249,115,22,0.15)'),
                    color: busAbsentFrom ? '#dc2626' : (routeFrom ? '#1d4ed8' : '#c2410c'),
                    ...(busAbsentFrom ? { '& .MuiChip-label': { textDecoration: 'line-through' } } : {}),
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "parents",
      headerName: t('students.parents_col'),
      flex: 1,
      minWidth: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const parents = params.row.parents || [];
        if (parents.length === 0) return <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8' }}>—</Typography>;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {parents.map((p) => (
              <Link key={p.profile_id} to={`/parent/${p.profile_id}`} style={{ textDecoration: 'none' }}>
                <Chip label={`${p.firstname} ${p.lastname}`} size="small"
                  sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', background: 'rgba(15,45,94,0.07)', color: NAV_BG, height: 22, cursor: 'pointer', '&:hover': { background: 'rgba(15,45,94,0.14)' } }} />
              </Link>
            ))}
          </Box>
        );
      },
    },
    {
      field: "id",
      headerName: t('students.actions'),
      width: 150,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={t('students.edit')} arrow>
            <Link to={`/student/${params.value}`} style={{ display: 'flex' }}>
              <IconButton size="small" sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { color: NAV_BG, background: 'rgba(15,45,94,0.08)' } }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title={t('absences.manage')} arrow>
            <IconButton size="small" sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { color: '#f97316', background: 'rgba(249,115,22,0.08)' } }} onClick={() => openAbsenceDialog(params)}>
              <CalendarMonthIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('students.send_message')} arrow>
            <IconButton size="small" sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { color: AMBER_DARK, background: `rgba(245,158,11,0.1)` } }} onClick={(e) => sendMessage(e, params)}>
              <MessageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('students.delete')} arrow>
            <IconButton size="small" sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.08)' } }} onClick={(e) => deleteStudent(e, params)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,45,94,0.07)' }}>
      <DataGrid
        rows={props.students}
        columns={columns}
        localeText={{ ...enUS.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: t('common.no_rows'), noResultsOverlayLabel: t('common.no_rows'), MuiTablePagination: { labelRowsPerPage: t('common.rows_per_page') } }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        slots={{ toolbar: CustomToolbar }}
        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 20 } }, sorting: { sortModel: [{ field: 'lastname', sort: 'asc' }, { field: 'firstname', sort: 'asc' }] } }}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        autoHeight
        getRowClassName={(params) => {
          if (params.row.is_absent_today) return 'row-absent-today';
          if (params.row.is_bus_absent_to || params.row.is_bus_absent_from) return 'row-bus-absent';
          if (!params.row.is_active) return 'row-inactive';
          return '';
        }}
        sx={{
          border: 'none',
          fontFamily: '"Outfit", sans-serif',
          '& .MuiDataGrid-columnHeaders': { background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`, borderBottom: 'none', borderRadius: 0 },
          '& .MuiDataGrid-columnHeaderTitle': { fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.04em', color: 'rgba(241,245,249,0.85)' },
          '& .MuiDataGrid-columnHeader': { '&:focus, &:focus-within': { outline: 'none' } },
          '& .MuiDataGrid-columnSeparator': { color: 'rgba(245,158,11,0.2)' },
          '& .MuiDataGrid-row': { transition: 'background 0.15s ease', '&:hover': { background: 'rgba(15,45,94,0.03)' }, '&.Mui-selected': { background: `rgba(245,158,11,0.06)`, '&:hover': { background: `rgba(245,158,11,0.1)` } } },
          '& .row-inactive': { background: '#fffbeb', '&:hover': { background: '#fef9e7' } },
          '& .row-absent-today': { background: 'rgba(239,68,68,0.06)', '&:hover': { background: 'rgba(239,68,68,0.1)' } },
          '& .row-bus-absent': { background: 'rgba(249,115,22,0.05)', '&:hover': { background: 'rgba(249,115,22,0.09)' } },
          '& .MuiDataGrid-cell': { fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#1e293b', borderColor: '#f1f5f9', '&:focus, &:focus-within': { outline: 'none' } },
          '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #e2e8f0', background: '#fafbfc' },
          '& .MuiTablePagination-root': { fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#475569' },
        }}
      />

      <SendMessageComponent name={studentName} studentId={studentId} open={open} setOpen={setOpen} />

      {/* ── Absence Dialog ── */}
      <Dialog open={absenceDialogOpen} onClose={() => setAbsenceDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthIcon sx={{ color: '#f97316', fontSize: 20 }} />
            {absenceChild ? t('absences.for', { name: absenceChild.name }) : t('absences.title')}
          </Box>
          <IconButton size="small" onClick={() => setAbsenceDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: NAV_BG, mb: 1.5 }}>
              {t('absences.add_absence')}
            </Typography>

            {/* Absence type toggle */}
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b', mb: 0.75 }}>{t('absences.type_label')}</Typography>
            <ToggleButtonGroup
              value={absenceType}
              exclusive
              onChange={(_, v) => { if (v) setAbsenceType(v); }}
              size="small"
              sx={{ mb: 1.5, '& .MuiToggleButton-root': { fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', textTransform: 'none', px: 1.5, py: 0.5, borderColor: '#e2e8f0', '&.Mui-selected': { background: 'rgba(15,45,94,0.1)', color: NAV_BG, fontWeight: 600, borderColor: NAV_BG } } }}
            >
              <ToggleButton value="full">{t('absences.type_full')}</ToggleButton>
              <ToggleButton value="bus">{t('absences.type_bus')}</ToggleButton>
            </ToggleButtonGroup>

            {/* Route checkboxes for bus-only */}
            {absenceType === 'bus' && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b', width: '100%' }}>{t('absences.routes_label')}</Typography>
                {absenceChild?.bus_to_school && (
                  <FormControlLabel
                    control={<Checkbox checked={skipTo} onChange={e => setSkipTo(e.target.checked)} size="small" sx={{ color: '#f97316', '&.Mui-checked': { color: '#f97316' } }} />}
                    label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem' }}>{t('absences.skip_to_school')}</Typography>}
                  />
                )}
                {absenceChild?.bus_from_school && (
                  <FormControlLabel
                    control={<Checkbox checked={skipFrom} onChange={e => setSkipFrom(e.target.checked)} size="small" sx={{ color: '#f97316', '&.Mui-checked': { color: '#f97316' } }} />}
                    label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem' }}>{t('absences.skip_from_school')}</Typography>}
                  />
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField
                label={t('absences.date_label')}
                type="date"
                size="small"
                value={absenceDate}
                onChange={(e) => setAbsenceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                label={t('absences.reason_label')}
                placeholder={t('absences.reason_placeholder')}
                size="small"
                value={absenceReason}
                onChange={(e) => setAbsenceReason(e.target.value)}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <Button
                variant="contained"
                disabled={!absenceDate || absenceSaving || (absenceType === 'bus' && !skipTo && !skipFrom)}
                onClick={handleAddAbsence}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: '#f97316', '&:hover': { background: '#ea6c0a' }, '&:disabled': { background: '#e2e8f0', color: '#94a3b8' } }}
              >
                {t('absences.save')}
              </Button>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: NAV_BG, mb: 1 }}>
              {t('absences.title')}
              <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#64748b', fontSize: '0.75rem' }}>
                ({absenceList.length})
              </Typography>
            </Typography>
            {absenceList.length === 0 ? (
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                {t('absences.no_absences')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 260, overflowY: 'auto' }}>
                {absenceList.map((a) => (
                  <Box key={a.absence_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '8px 12px', borderRadius: '10px', bgcolor: a.absence_type === 0 ? '#fff7ed' : '#fef3c7', border: `1px solid ${a.absence_type === 0 ? '#fed7aa' : '#fde68a'}` }}>
                    <CalendarMonthIcon sx={{ fontSize: 15, color: '#f97316', flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#c2410c', flex: 1 }}>
                      {a.date}{a.reason ? ` — ${a.reason}` : ''}
                    </Typography>
                    <Chip
                      label={a.absence_type === 0 ? t('absences.chip_full') : t('absences.chip_bus')}
                      size="small"
                      sx={{ height: 16, fontSize: '0.62rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: a.absence_type === 0 ? 'rgba(239,68,68,0.12)' : 'rgba(249,115,22,0.15)', color: a.absence_type === 0 ? '#dc2626' : '#c2410c' }}
                    />
                    <IconButton size="small" onClick={() => handleDeleteAbsence(a.absence_id)} sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmDialogComponent
        handleAction={doDeleteStudent}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        text={studentName}
        texttitle={t('students.delete_confirm')}
      />
    </Paper>
  );
};

export default StudentsComponent;
